import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { Skeleton, Tag } from 'antd'
import { HStack, Icon, Input, Modal, VStack } from 'govwell-ui'
import { useMenuKeyboardShortcuts } from 'govwell-ui/components/Menu/use-menu-keyboard-shortcuts'
import { v4 as uuid } from 'uuid'

import { navigate, routes } from '@redwoodjs/router'

import EmptyState from 'src/components/EmptyState'
import { IconOnlyButton, RecordAddress } from 'src/components/shared/StyledComponents'
import Text, { TextSize } from 'src/components/Typography/Text'
import UniversalSearchTypeRadioGroup from 'src/components/UniversalSearchModal/UniversalSearchTypeRadio'
import {
  UniversalSearchOption,
  UniversalSearchOptionType,
  UniversalSearchType,
} from 'src/components/UniversalSearchModal/util'
import { useIsGov } from 'src/layouts/AppStateContextLayout/utils'
import { getUrlForRecord } from 'src/utils/records'

import {
  UniversalSearchRecordFragment,
  useUniversalSearchQuery,
} from '../../fetch/universal-search'
import useDebounce from '../../hooks/use-debounce'
import { UseDisclosureReturn } from '../../hooks/use-disclosure'
import { entityUrl } from '../TableViews/EntityDisplay'

import UniversalSearchModalFooter from './UniversalSearchModalFooter'
import UniversalSearchResultTemplate from './UniversalSearchResultTemplate'

type Props = {
  modalState: UseDisclosureReturn
  onCloseFocusRef: React.RefObject<HTMLElement>
}

const UniversalSearchModal = ({ modalState, onCloseFocusRef }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [searchType, setSearchType] = useState<UniversalSearchType>({
    label: 'All',
    mode: 'all',
  })
  const debouncedQuery = useDebounce(query, 500)

  useEffect(() => {
    if (!modalState.isOpen) {
      setQuery('')
    }
    let timeout: NodeJS.Timeout
    if (modalState.isOpen) {
      timeout = setTimeout(() => {
        inputRef.current?.focus()
      })
    }
    return () => timeout && clearTimeout(timeout)
  }, [modalState.isOpen])

  const isGov = useIsGov()

  const { data, isFetching } = useUniversalSearchQuery(debouncedQuery)
  const options: UniversalSearchOption[] = useMemo(() => {
    if (!data?.universalSearch) {
      return []
    }
    const results: UniversalSearchOption[] = []
    const addAddresses = () =>
      data.universalSearch.addresses.forEach((address) =>
        results.push({
          type: UniversalSearchOptionType.Address,
          title: 'Address',
          subtitle: address,
          subtitleSortString: address,
          url: routes.staffAddressOverview({ addressDisplayName: encodeURIComponent(address) }),
          uuid: uuid(),
        })
      )
    const addParcels = () =>
      data.universalSearch.parcels.forEach((parcel) =>
        results.push({
          type: UniversalSearchOptionType.Parcel,
          title: 'Parcel',
          subtitle: parcel,
          subtitleSortString: parcel,
          url: routes.staffParcelOverview({
            parcelDisplayName: encodeURIComponent(parcel),
          }),
          uuid: uuid(),
        })
      )
    const childRecordsMap = new Map<string, UniversalSearchOption[]>()

    const createRecordOption = (
      record: UniversalSearchRecordFragment,
      isChildRecord: boolean,
      parentUuid?: string
    ): UniversalSearchOption => {
      const recordTemplate = record.recordTemplate
      const recordType = recordTemplate?.recordType
      return {
        type: UniversalSearchOptionType.Record,
        title: `${recordType?.displayName} · ${recordTemplate?.name}`,
        subtitle: (
          <VStack>
            <HStack align="center" gap={2}>
              <Text>#{record.displayIdentifier}</Text>
              {!!record.mainBackwardsCompatibleLocationInformation?.address?.str && (
                <>
                  <Text>·</Text>
                  <RecordAddress record={record} />
                </>
              )}
            </HStack>
            {/* currently this whole modal is only used by gov,
             but this check is extra defensive to not show tags to citizens
              in the case that we open this modal up to citizens in the future*/}
            {isGov
              ? (record.tags || []).map((tag: string, index: number) => (
                  <Tag key={index}>{tag}</Tag>
                ))
              : null}
          </VStack>
        ),
        subtitleSortString: `${
          record.issuedIdentifier ?? record.identifier
        }${record.mainBackwardsCompatibleLocationInformation?.address?.str ?? ''}`,
        url: getUrlForRecord({
          currentUserHasAccess: true,
          isDraft: !!record.isDraft,
          organizationSlug: record.organization?.slug,
          recordUuid: record.uuid,
        }),
        uuid: uuid(),
        isChildRecord,
        parentUuid,
      }
    }

    const addRecords = (recordTypeId?: number) =>
      data.universalSearch.records
        .filter((record) => !recordTypeId || record.recordTemplate?.recordType?.id === recordTypeId)
        .forEach((record) => {
          // Add parent record to results
          const parentOption = createRecordOption(record, false)
          results.push(parentOption)

          // Store child records separately, keyed by parent uuid
          const childRecords = record.childRecords ?? []
          if (childRecords.length > 0) {
            const childOptions = childRecords.map((childRecord) =>
              createRecordOption(childRecord, true, parentOption.uuid)
            )
            childRecordsMap.set(parentOption.uuid, childOptions)
          }
        })
    const addViolations = () =>
      data.universalSearch.violations.forEach((violation) =>
        results.push({
          type: UniversalSearchOptionType.Violation,
          title: 'Violation',
          subtitle: `#${violation.identifier} · ${violation.address}`,
          subtitleSortString: `${violation.identifier}${violation.address}`,
          url: routes.codeComplaint({ identifier: encodeURIComponent(violation.identifier) }),
          uuid: uuid(),
        })
      )

    const addEntities = (searchOptionType: UniversalSearchOptionType) =>
      data.universalSearch[
        searchOptionType === UniversalSearchOptionType.Address
          ? 'addressEntities'
          : 'parcelEntities'
      ].forEach((entity) =>
        results.push({
          type: searchOptionType,
          title: entity.name,
          subtitle: entity.name,
          subtitleSortString: entity.name,
          url: entityUrl(entity) ?? '',
          uuid: uuid(),
        })
      )

    const addAddressEntities = () => addEntities(UniversalSearchOptionType.Address)
    const addParcelEntities = () => addEntities(UniversalSearchOptionType.Parcel)

    switch (searchType.mode) {
      case 'all':
        addAddresses()
        addParcels()
        addRecords()
        addViolations()
        addAddressEntities()
        addParcelEntities()
        break
      case 'addresses':
        addAddresses()
        addAddressEntities()
        break
      case 'parcels':
        addParcels()
        addParcelEntities()
        break
      case 'records':
        addRecords(searchType.recordTypeId)
        break
      case 'violations':
        addViolations()
        break
    }

    // Sort all results alphabetically (same as original behavior)
    const sortedResults = results.sort(
      (a, b) =>
        a.title.localeCompare(b.title) || a.subtitleSortString.localeCompare(b.subtitleSortString)
    )

    // Insert child records after their parent records
    const finalResults: UniversalSearchOption[] = []
    for (const result of sortedResults) {
      finalResults.push(result)
      // If this is a parent record with children, insert them after
      const children = childRecordsMap.get(result.uuid)
      if (children) {
        finalResults.push(...children)
      }
    }

    return finalResults
  }, [data, isGov, searchType.mode, searchType.recordTypeId])

  const handleOptionClicked = useCallback(
    (option: UniversalSearchOption, openInNewTab?: boolean) => {
      if (openInNewTab) {
        window.open(option.url)
      } else {
        navigate(option.url)
        modalState.close()
      }
    },
    [modalState]
  )

  const [activeIndex, setActiveIndex] = useState(0)
  const { menuItemRefs } = useMenuKeyboardShortcuts({
    activeIndex,
    count: options.length,
    isEnabled: modalState.isOpen,
    onEscape: modalState.close,
    onSelect: (e) => {
      const option = options[activeIndex]
      if (option) {
        handleOptionClicked(option, e.ctrlKey || e.metaKey)
      }
    },
    setActiveIndex,
  })
  useEffect(() => {
    setActiveIndex(0)
    const container = containerRef.current
    if (container) {
      container.scrollTop = 0
    }
  }, [data, setActiveIndex])

  return (
    <Modal onClose={modalState.close} width="684px" onCloseFocusRef={onCloseFocusRef}>
      <Modal.Content className="!pb-2">
        <div
          ref={containerRef}
          style={{
            height: '60vh',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'white',
              overflow: 'visible',
            }}
          >
            <HStack gap={2}>
              <Input
                ariaLabel="Search input"
                value={query}
                onValueChange={setQuery}
                prefixIcon={faSearch}
                isLoading={isFetching}
                width="100%"
                placeholder="Search record #, parcel ID, or address"
                size="lg"
                autoFocus
                ref={inputRef}
              />
              <IconOnlyButton
                onClick={modalState.close}
                icon={<Icon.FaXmark />}
                className="!max-h-[24px] !max-w-[24px] !p-0"
              />
            </HStack>
            <UniversalSearchTypeRadioGroup
              searchType={searchType}
              onSearchTypeChange={setSearchType}
            />
            {!!options.length && (
              <HStack justify="center" className="pb-1.5">
                <Text size={TextSize.ExtraSmall} colorToken="colorTextTertiary">
                  Showing closest matching results
                </Text>
              </HStack>
            )}
          </div>
          <div
            className="flex flex-1 flex-col overflow-y-auto"
            role="menu"
            tabIndex={0}
            aria-activedescendant={options[activeIndex]?.uuid}
          >
            {isFetching && <Skeleton active />}
            {!isFetching &&
              options.map((option, index) => (
                <UniversalSearchResultTemplate
                  key={option.uuid}
                  isActive={activeIndex === index}
                  onMouseMove={() => setActiveIndex(index)}
                  option={option}
                  ref={(el) => menuItemRefs.current.set(index, el)}
                />
              ))}
          </div>
          {!options.length && (
            <EmptyState hideBorder>
              <EmptyState.Image />
              <EmptyState.Message>No results</EmptyState.Message>
            </EmptyState>
          )}
          <div
            style={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'white',
              zIndex: 1,
            }}
          >
            <UniversalSearchModalFooter count={options.length} />
          </div>
        </div>
      </Modal.Content>
    </Modal>
  )
}

export default React.memo(UniversalSearchModal)
